import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import {
  loadEnvironmentVariablesInURLByDocsId,
  saveEnvironmentVariablesInURLByDocsId,
} from '@/shared/constant/local-storage/use-environment/UseEnvironmentInSearchStorage';
import { useNavigation, useSearchParams } from '@remix-run/react';
import React, { useEffect, useMemo, useRef } from 'react';

export const useVariablesFromUrl = () => {
  const [urlSearchParams] = useSearchParams();
  const urlSearchParamsStr = useMemo(() => urlSearchParams.toString(), [urlSearchParams]);

  const saveEnvironmentVariables = useDocsDataModelStore((x) => x.saveEnvironmentVariables);
  const loadClientEnvironment = useDocsDataModelStore((x) => x.loadClientEnvironment);
  const isClientEnvironmentLoaded = useDocsDataModelStore((x) => x.isClientEnvironmentLoaded);
  const isSearchVariablesInURLPrepared = useDocsDataModelStore(
    (x) => x.isSearchVariablesInURLPrepared,
  );

  const docsId = useDocsDataModelStore((x) => x.docsIdTypeData.onlineId);

  const { environmentList } = useDocsDataModelStore((x) => x.environments);

  const navigation = useNavigation();
  React.useEffect(() => {
    if (navigation.state === 'idle') {
      // change the environment based on client cache
      loadClientEnvironment();
    }
  }, [navigation.state, loadClientEnvironment]);

  const lastSearchParams = useRef<string>();
  useEffect(() => {
    if (lastSearchParams.current === urlSearchParamsStr) {
      return;
    }
    if (!isClientEnvironmentLoaded || !isSearchVariablesInURLPrepared) {
      return;
    }
    if (!environmentList || environmentList.length === 0) {
      return;
    }
    lastSearchParams.current = urlSearchParamsStr;
    try {
      const lastEnvironmentVariablesInURL = loadEnvironmentVariablesInURLByDocsId(docsId) || [];
      if (!lastEnvironmentVariablesInURL || lastEnvironmentVariablesInURL.length <= 0) {
        return;
      }
      const environmentVarList = lastEnvironmentVariablesInURL.reduce(
        (acc, item) => {
          acc[item[0]] = item[1];
          return acc;
        },
        {} as Record<string, string>,
      );
      let finalVariables;
      for (let index = 0; index < environmentList.length; index += 1) {
        const environment = environmentList[index];
        if (!environment) {
          continue;
        }

        Object.entries(environmentVarList).forEach(([key, value]) => {
          const isExistedIndex = environment.variables.findIndex((item) => item.name === key);
          if (isExistedIndex > -1) {
            environment.variables[isExistedIndex]!.value = value;
          } else {
            environment.variables.push({
              name: key,
              value,
            });
          }
        });
        finalVariables = environment.variables;
        if (!!finalVariables) {
          saveEnvironmentVariables(environment.id, finalVariables);
        }
      }
      saveEnvironmentVariablesInURLByDocsId(docsId, undefined);
    } catch (error) {
      console.warn('Failed to apply environment variables from URL', error);
    }
  }, [
    isClientEnvironmentLoaded,
    isSearchVariablesInURLPrepared,
    urlSearchParamsStr,
    environmentList,
    saveEnvironmentVariables,
    docsId,
  ]);
};
